import { Flex } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { useStickyCard } from '@feathr/components';
import type { ListResponse } from '@feathr/rachis';
import { TargetAudienceOverview, useAudienceBreakdown, useFlags } from '@feathr/report_components';

import { type IUniqueEmailsResult } from '../AddWizard.useReconcileUniqueEmails';
import AdWizardAudienceStepContent from './AdWizardAudienceStep.Content';
import NewAudience from './AdWizardAudienceStep.NewAudience';

import * as styles from './AdWizardAudienceStep.css';

interface IProps {
  campaign: Campaign;
  targetings: ListResponse<Targeting>;
  emailValidation: IUniqueEmailsResult;
}

function AdWizardAudienceStep({
  campaign,
  targetings: targetingsList,
  emailValidation,
}: Readonly<IProps>): JSX.Element {
  const { showLookalikeAudienceTargeting } = useFlags();
  const audienceData = useAudienceBreakdown({
    targetings: targetingsList,
    campaign,
  });

  const { hasBuildReachAwarenessPP } = useFlags();

  const { isOmniCampaign, isOmniReachCampaign } = campaign;
  const isOmniReachCampaignWithFlag = (isOmniReachCampaign && hasBuildReachAwarenessPP) ?? false;

  const { leftContentRef, stickyWrapperHeight } = useStickyCard({
    enabled: isOmniCampaign,
    dependencies: [targetingsList, audienceData],
  });

  const stepContent = (
    <AdWizardAudienceStepContent
      campaign={campaign}
      emailValidation={emailValidation}
      targetingsList={targetingsList}
    />
  );

  const wrappedStepContent = isOmniCampaign ? (
    <div className={styles.left} ref={leftContentRef}>
      {stepContent}
    </div>
  ) : (
    stepContent
  );

  return (
    <Flex className={styles.root} justify={isOmniCampaign ? 'space-between' : 'center'}>
      {wrappedStepContent}
      {isOmniCampaign && !isOmniReachCampaignWithFlag && (
        <div
          className={styles.stickyWrapper}
          style={{
            // Dynamic height controls when sticky element "unsticks" and becomes static.
            // Height = left content height + buffer, so right column stays sticky while
            // scrolling through left content, then unsticks near the end for alignment.
            height: stickyWrapperHeight,
          }}
        >
          <TargetAudienceOverview
            className={styles.stickyRight}
            showLookalikeAudienceTargeting={showLookalikeAudienceTargeting}
            width={'narrow'}
            {...audienceData}
          />
        </div>
      )}

      {isOmniReachCampaignWithFlag && (
        <div
          className={styles.stickyWrapper}
          style={{
            // Dynamic height controls when sticky element "unsticks" and becomes static.
            // Height = left content height + buffer, so right column stays sticky while
            // scrolling through left content, then unsticks near the end for alignment.
            height: stickyWrapperHeight,
          }}
        >
          <NewAudience
            campaign={campaign}
            className={styles.stickyRight}
            targetings={targetingsList}
          />
        </div>
      )}
    </Flex>
  );
}

export default observer(AdWizardAudienceStep);
